import {
  PrimaryControlsStripExpansionHost,
  usePrimaryControlsStripExpansionAnimatedHeight,
  usePrimaryControlsStripExpansionHasContent,
  usePrimaryControlsStripExpansionState,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.expansion";
import {
  PrimaryControlsStripTitleHost,
  usePrimaryControlsStripTitleHostGate,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.title";
import { useIsPrimaryControlsStripExpansionButtonHidden } from "@-/dev.improve.haniverse.routes.nav._context";
import {
  NavAccountDrawerPopover,
  type NavAccountDrawerPopoverAction,
} from "@-/dev.improve.haniverse.routes.nav._components.account-drawer";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { usePathname } from "@-/com.npmjs.expo-router";
import { Pressable, View } from "@-/com.npmjs.react-native";
import Animated from "@-/com.npmjs.react-native-reanimated";
import { useSearchPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.search.navigator";
import { getWebSectionLabel, isWebSearchPath } from "#web-section";
import { WebPrimaryControlsStripPageControlsHost } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";

/**
 * Opens Search, which belongs to Browse on web: the header then reads "Browse" and Browse's
 * sidebar shows with Search selected.
 */
function WebPrimaryControlsStripSearchButton() {
  const searchPageNavigator = useSearchPageNavigator();
  const pathname = usePathname();
  const isSearchActive = isWebSearchPath(pathname);
  return (
    <Pressable
      accessibilityLabel="Search characters"
      accessibilityRole="button"
      accessibilityState={{ selected: isSearchActive }}
      className={`size-10 shrink-0 items-center justify-center rounded-full hover:bg-accent active:bg-accent ${isSearchActive ? "bg-accent" : ""}`}
      hitSlop={6}
      // Web e2e flows select the control by DOM id.
      nativeID="Search characters"
      onPress={() => {
        searchPageNavigator.push.mutate();
      }}
      testID="nav-header-search"
    >
      <Icon className="text-foreground" name="MdSearch" size={22} />
    </Pressable>
  );
}

/**
 * Web-only site header: the chrome that replaces the app's drawer and bottom tabs on web.
 *
 * The left slot is one control, the learner's avatar beside the name of the section they are on
 * ("Settings", "Browse"), which opens the account popover: web's equivalent of the native account
 * drawer, with the same profile surface and tabs (`NavAccountDrawerPopover`). Focused screen
 * titles follow it as a breadcrumb segment, unless they only repeat the section's name. The right
 * slot holds the focused page's controls beside a search button, which opens Browse's Search.
 * Pages with sub-sections list them in a left sidebar rather than a second header row
 * (`WebSectionSidebarLayout`).
 *
 * Screens that publish `PrimaryControlsStripExpansion` content (learn drill controls, browse
 * filters) still get their strip below the header row, identical to `PrimaryControlsStrip`'s
 * expansion strip: that content is functional chrome, not navigation. The popover toggles it.
 */
export function WebPrimaryControlsStrip() {
  const pathname = usePathname();
  const sectionLabel = getWebSectionLabel(pathname);
  const hasExpansionContent = usePrimaryControlsStripExpansionHasContent();
  const isPrimaryControlsStripExpansionButtonHidden =
    useIsPrimaryControlsStripExpansionButtonHidden();
  const { isExpanded, toggleExpanded } = usePrimaryControlsStripExpansionState();
  const showsExpansionAction = hasExpansionContent && !isPrimaryControlsStripExpansionButtonHidden;
  // The breadcrumb follows the section name, unless it only repeats it ("Settings").
  const titleHostGate = usePrimaryControlsStripTitleHostGate({
    redundantTitle: sectionLabel ?? undefined,
  });
  const accountActions: NavAccountDrawerPopoverAction[] = showsExpansionAction
    ? [
        {
          icon: "RiExpandUpDownFill",
          label: isExpanded ? "Collapse primary controls" : "Expand primary controls",
          onPress: toggleExpanded,
          testID: "nav-account-menu-primary-controls",
        },
      ]
    : [];

  const { onContentLayout: handleExpansionContentLayout, wrapperStyle: expansionWrapperStyle } =
    usePrimaryControlsStripExpansionAnimatedHeight();

  return (
    <View className="border-b border-border bg-background">
      <View className="h-14 flex-row items-center justify-between gap-4 px-4">
        <View className="h-full min-w-0 flex-1 flex-row items-center gap-3">
          <NavAccountDrawerPopover actions={accountActions} label={sectionLabel} />
          {titleHostGate !== null && (
            <PrimaryControlsStripTitleHost
              align="start"
              gate={titleHostGate}
              presentation="breadcrumb"
            />
          )}
        </View>
        <View className="shrink-0 flex-row items-center gap-2">
          <WebPrimaryControlsStripPageControlsHost />
          <WebPrimaryControlsStripSearchButton />
        </View>
      </View>
      <Animated.View className="relative overflow-hidden" style={expansionWrapperStyle}>
        <View className="absolute inset-x-0 px-4" onLayout={handleExpansionContentLayout}>
          <PrimaryControlsStripExpansionHost />
        </View>
      </Animated.View>
    </View>
  );
}
